import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import { useAuth } from '@/layouts/RootLayout';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { StatsSkeleton } from '@/components/PageSkeleton';
import { ErrorState, Metric, PageHeader, Panel, StatusBadge } from '@/components/kit';
import { ListBody } from '@/components/inputs';
import RenewButton from '@/components/account/RenewButton';
import { VerifiedBadge } from '@/components/verifier/VerifierResult';
import { ORDER_STATUS_STYLES, TONE_STYLES } from '@/shared/constants';
import { ANNOUNCEMENT_FIELDS, ORDER_LIST_FIELDS, aggOf } from '@/shared/fields';
import { RENEW_WARN_DAYS, money, orderTotal, planSummary, timeLeft } from '@/shared/pricing';
import { findMyCustomer } from '@/shared/account';
import { rows } from '@/shared/data';
import { MIN_REFERRAL_MONTHS, isPaidLine, normUsername } from '@/shared/referrals';
import { loadMyReferral } from '@/shared/referralLink';
import ReferralLinkBox from '@/components/account/ReferralLinkBox';
import { cn } from '@/lib/utils';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';
import { useT, translate } from '@/shared/i18n';
import { A } from '@/shared/locales/account/keys';
import { planSummaryT, timeLeftT } from '@/shared/accountText';

const { and, eq, gt, isIn, isNotNull, isNull, lte, or } = window.ApperSDK.CoreSDK;

export const route = { path: '/account', layout: 'owner', access: "profile === 'Customer_c'" };
export const nav = { icon: 'Ghost', label: 'Overview', section: 'My account', profiles: ['Customer_c'], order: 10 };

const COUNT = [{ field: 'Id', fn: 'Count', alias: 'count' }];

async function loadStats() {
  const [orders, tickets] = await Promise.all([
    sdk
      .table('orders_c')
      .select([{ field: 'Id' }])
      .aggregate([
        { id: 'all', fields: COUNT },
        { id: 'live', fields: COUNT, where: isIn('status_c', ['Active', 'Trial']) },
        { id: 'waiting', fields: COUNT, where: isIn('status_c', ['Pending', 'Invoiced']) },
      ])
      .limit(1)
      .fetch(),
    sdk
      .table('tickets_c')
      .select([{ field: 'Id' }])
      .aggregate([{ id: 'open', fields: COUNT, where: isIn('status_c', ['Open', 'Answered']) }])
      .limit(1)
      .fetch(),
  ]);
  if (!orders.success) throw new Error(orders.message || translate(A.loadOrders));
  if (!tickets.success) throw new Error(tickets.message || translate(A.loadTickets));
  const o = aggOf(orders);
  const t = aggOf(tickets);
  return { all: o('all'), live: o('live'), waiting: o('waiting'), open: t('open') };
}

function loadNudges() {
  const soon = new Date(Date.now() + RENEW_WARN_DAYS * 86400000).toISOString();
  return sdk
    .table('orders_c')
    .select(ORDER_LIST_FIELDS)
    .where(and(isIn('status_c', ['Active', 'Trial']), lte('ends_at_c', soon)))
    .orderBy('ends_at_c')
    .limit(6)
    .fetch()
    .then(rows);
}

function loadRecent() {
  return sdk.table('orders_c').select(ORDER_LIST_FIELDS).orderByDesc('CreatedOn').page(1, 4).fetch().then(rows);
}

function loadAnnouncement() {
  const now = new Date().toISOString();
  return sdk
    .table('announcements_c')
    .select(ANNOUNCEMENT_FIELDS)
    .where(
      and(eq('active_c', true), isIn('show_on_c', ['Account', 'Both']), or(isNull('ends_at_c'), gt('ends_at_c', now))),
    )
    .orderByDesc('CreatedOn')
    .limit(1)
    .fetch()
    .then(rows);
}

function loadMyLines() {
  return sdk
    .table('orders_c')
    .select(['Name', 'status_c', 'ends_at_c', 'line_username_c', 'gift_months_c', 'line_verified_at_c'])
    .where(and(eq('status_c', 'Active'), isNotNull('line_username_c')))
    .limit(20)
    .fetch()
    .then(rows);
}

const seenKey = (id) => `gc:referrals-seen:${id}`;

/** How many referrals arrived since the buyer last looked, remembered per browser. */
function useNewReferrals(customer) {
  const [fresh, setFresh] = useState(0);
  const id = customer?.Id;
  const earned = Number(customer?.referrals_earned_c || 0);
  useEffect(() => {
    if (!id) return;
    let seen = 0;
    try {
      seen = Number(window.localStorage.getItem(seenKey(id)) || 0) || 0;
    } catch {
      return;
    }
    if (earned > seen) {
      const gained = earned - seen;
      setFresh(gained);
      toast.success(translate(A.earned, { count: gained }), {
        description: translate(A.friendLive),
      });
    }
    try {
      window.localStorage.setItem(seenKey(id), String(earned));
    } catch {
      // private mode: the badge still shows the total
    }
  }, [id, earned]);
  return fresh;
}

function ReferralPill({ count, fresh, className }) {
  const { t } = useT();
  if (!count) return null;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border border-highlight/40 bg-highlight/15 px-2.5 py-0.5 text-xs font-semibold text-highlight tabular-nums',
        className,
      )}
      title={t(A.refsEarnedN, { count })}
    >
      <ApperIcon name="Gift" size={14} />
      {t(A.refsN, { count })}
      {fresh > 0 && <span className="rounded-full bg-success px-1.5 text-[10px] uppercase tracking-wide text-success-foreground">{t(A.newTag)}</span>}
    </span>
  );
}

const QUICK = [
  { to: '/account/verifier', icon: 'ShieldCheck', label: A.navVerifier, hint: A.qVerH },
  { to: '/account/tickets', icon: 'LifeBuoy', label: A.qHelp, hint: A.qHelpH },
  { to: '/account/receipts', icon: 'Receipt', label: A.navReceipts, hint: A.qRecH },
  { to: '/account/reviews', icon: 'Star', label: 'Reviews', hint: A.qRevH },
  { to: '/account/preferences', icon: 'SlidersHorizontal', label: A.navPrefs, hint: A.qPrefH },
  { to: '/install', icon: 'Smartphone', label: 'Get the app', hint: A.qAppH },
];

export default function Overview() {
  const { t } = useT();
  const { user } = useAuth();
  const stats = useFetch(loadStats, []);
  const nudges = useFetch(loadNudges, []);
  const recent = useFetch(loadRecent, []);
  const notice = useFetch(loadAnnouncement, []);
  const me = useFetch(() => findMyCustomer(user), [user?.emailAddress]);
  const referrals = Number(me.data?.referrals_earned_c || 0);
  const freshReferrals = useNewReferrals(me.data);
  const lines = useFetch(loadMyLines, []);
  const myRef = useFetch(loadMyReferral, []);
  const refLink = myRef.data?.username ? myRef.data : null;
  const lineRows = lines.data ?? [];
  const usernames = [...new Set(lineRows.filter((o) => isPaidLine(o)).map((o) => o.line_username_c.trim()))];
  const freeMonths = lineRows.reduce((n, o) => n + (Number(o.gift_months_c) || 0), 0);
  const verifiedAt = lineRows
    .map((o) => o.line_verified_at_c)
    .filter(Boolean)
    .sort()
    .pop();

  const announcement = (notice.data ?? [])[0];
  const nudgeRows = nudges.data ?? [];
  const recentRows = recent.data ?? [];
  const s = stats.data;

  function refreshOrders() {
    nudges.run();
    recent.run();
    stats.run();
  }

  async function copyCode(value) {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t(A.copied));
    } catch {
      toast.error(t(A.cantCopy));
    }
  }

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        eyebrow={t('My account')}
        title={user?.firstName ? t(A.welcomeName, { name: user.firstName }) : t('Welcome back')}
        description={t(A.overviewDesc)}
        actions={
          <>
            {verifiedAt && (
              <Link
                to="/account/verifier"
                className="rounded-full focus-visible:outline-2 focus-visible:outline-ring"
                aria-label="Your line is verified genuine Ghost Cable. Open the IPTV Verifier"
              >
                <VerifiedBadge at={verifiedAt} className="py-1.5 text-sm" />
              </Link>
            )}
            <a href="#refer-a-friend" className="rounded-full focus-visible:outline-2 focus-visible:outline-ring">
              <ReferralPill count={referrals} fresh={freshReferrals} className="py-1.5 text-sm" />
            </a>
            <Button asChild>
              <Link to="/checkout">
                <ApperIcon name="Plus" size={16} />
                {t(A.newOrder)}
              </Link>
            </Button>
          </>
        }
      />

      {announcement && (
        <div
          className={cn(
            'mb-6 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm',
            TONE_STYLES[announcement.tone_c] ?? TONE_STYLES.Info,
          )}
          role="status"
        >
          <ApperIcon name={announcement.tone_c === 'Outage' ? 'TriangleAlert' : 'Megaphone'} size={18} className="mt-0.5 shrink-0" />
          <p className="text-foreground">{announcement.Name}</p>
        </div>
      )}

      {stats.loading && s === undefined ? (
        <StatsSkeleton count={4} />
      ) : stats.error ? (
        <ErrorState message={stats.error} onRetry={stats.run} />
      ) : (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Metric label={t(A.mLive)} value={s?.live ?? 0} hint={t(A.mLiveH)} icon="Radio" />
          <Metric label={t(A.mWait)} value={s?.waiting ?? 0} hint={t(A.mWaitH)} icon="Hourglass" tone="text-warning" />
          <Metric label={t(A.mOpen)} value={s?.open ?? 0} hint={t(A.mOpenH)} icon="LifeBuoy" tone="text-highlight" />
          <Metric label={t(A.navOrders)} value={s?.all ?? 0} hint={t(A.mAllH)} icon="Package" />
        </div>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-6">
          <Panel title={t(A.endingSoon)} icon="Hourglass" bodyClassName="py-1">
            <ListBody
              loading={nudges.loading}
              data={nudges.data}
              error={nudges.error}
              run={nudges.run}
              rows={2}
              isEmpty={!nudgeRows.length}
              empty={{
                icon: 'ShieldCheck',
                title: t(A.nothingEnding),
                description: t(A.showDays, { days: RENEW_WARN_DAYS }),
              }}
            >
              <ul className="divide-y divide-border">
                {nudgeRows.map((o) => {
                  const ended = timeLeft(o.ends_at_c) === 'Ended';
                  const left = timeLeftT(o.ends_at_c, t);
                  return (
                    <li key={o.Id} className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <Link to={`/account/orders/${o.Id}`} className="font-mono text-sm text-primary hover:underline">
                          {o.Name}
                        </Link>
                        <p className="truncate text-sm">{planSummaryT(o, t)}</p>
                        <p className={cn('text-xs', ended ? 'text-destructive' : 'text-warning')}>
                          {left} · {t(A.endsOn, { date: formatLocalDate(o.ends_at_c, DATE_FORMATS.SHORT) })}
                        </p>
                      </div>
                      <RenewButton order={o} onDone={refreshOrders} />
                    </li>
                  );
                })}
              </ul>
            </ListBody>
          </Panel>

          <Panel
            title={t(A.recent)}
            icon="Package"
            bodyClassName="py-1"
            actions={
              <Button variant="ghost" size="sm" asChild>
                <Link to="/account/orders">{t(A.allOrders)}</Link>
              </Button>
            }
          >
            <ListBody
              loading={recent.loading}
              data={recent.data}
              error={recent.error}
              run={recent.run}
              rows={3}
              isEmpty={!recentRows.length}
              empty={{
                icon: 'Ghost',
                title: t(A.noOrders),
                description: t(A.noOrdersP),
                action: (
                  <Button asChild size="sm">
                    <Link to="/checkout">{t(A.startOrder)}</Link>
                  </Button>
                ),
              }}
            >
              <ul className="divide-y divide-border">
                {recentRows.map((o) => (
                  <li key={o.Id} className="flex items-center justify-between gap-3 py-3">
                    <div className="min-w-0">
                      <Link to={`/account/orders/${o.Id}`} className="font-mono text-sm text-primary hover:underline">
                        {o.Name}
                      </Link>
                      <p className="truncate text-sm text-muted-foreground">{planSummaryT(o, t)}</p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <StatusBadge value={o.status_c} styles={ORDER_STATUS_STYLES} />
                      <span className="text-sm tabular-nums">{money(orderTotal(o))}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </ListBody>
          </Panel>
        </div>

        <div className="space-y-6">
          <div id="refer-a-friend" className="scroll-mt-20">
          <Panel
            title={t(A.refer)}
            icon="Gift"
            bodyClassName="py-4"
            actions={<ReferralPill count={referrals} fresh={freshReferrals} />}
          >
            {me.loading && me.data === undefined ? (
              <Skeleton className="h-10 w-full" />
            ) : me.error ? (
              <ErrorState message={me.error} onRetry={me.run} />
            ) : me.data?.referral_code_c || usernames.length || refLink ? (
              <>
                <p className="text-sm text-muted-foreground">
                  {usernames.length || refLink
                    ? t(A.shareLink, { months: MIN_REFERRAL_MONTHS })
                    : t(A.shareCode)}
                </p>
                {refLink && (
                  <ReferralLinkBox
                    username={refLink.username}
                    url={refLink.url}
                    live={refLink.live}
                    compact
                    className="mt-3"
                  />
                )}
                {refLink && (
                  <Button asChild variant="outline" size="sm" className="mt-3 w-full">
                    <Link to="/account/referrals">
                      <ApperIcon name="Image" size={16} />
                      {t(A.sharePic)}
                    </Link>
                  </Button>
                )}
                {usernames.filter((name) => normUsername(name) !== refLink?.username).map((name) => (
                  <div
                    key={name}
                    className="mt-3 flex items-center justify-between gap-2 rounded-md border border-dashed border-highlight/50 bg-highlight/5 px-3 py-2"
                  >
                    <span className="min-w-0">
                      <span className="block text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                        {refLink ? t(A.alsoWorks) : t(A.yourUsername)}
                      </span>
                      <span className="block truncate font-mono text-highlight">{name}</span>
                    </span>
                    <Button variant="ghost" size="sm" onClick={() => copyCode(name)}>
                      <ApperIcon name="Copy" size={16} />
                      {t('Copy')}
                    </Button>
                  </div>
                ))}
                {me.data?.referral_code_c && (
                  <div className="mt-3 flex items-center justify-between gap-2 rounded-md border border-dashed border-highlight/50 bg-highlight/5 px-3 py-2">
                    <span className="min-w-0">
                      {usernames.length > 0 && (
                        <span className="block text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{t(A.orCode)}</span>
                      )}
                      <span className="block font-mono tracking-[0.18em] text-highlight">{me.data.referral_code_c}</span>
                    </span>
                    <Button variant="ghost" size="sm" onClick={() => copyCode(me.data.referral_code_c)}>
                      <ApperIcon name="Copy" size={16} />
                      {t('Copy')}
                    </Button>
                  </div>
                )}
                <div
                  className={cn(
                    'mt-3 flex items-center gap-3 rounded-md border px-3 py-2.5',
                    freshReferrals > 0 ? 'border-success-border bg-success-muted' : 'border-border',
                  )}
                  aria-live="polite"
                >
                  <span
                    className={cn(
                      'font-heading text-3xl leading-none tabular-nums',
                      referrals ? 'text-highlight' : 'text-muted-foreground',
                    )}
                  >
                    {referrals}
                  </span>
                  <span className="min-w-0 flex-1 text-sm">
                    <span className="block font-medium">{t(A.refEarnedLbl, { count: referrals })}</span>
                    <span className="block text-xs text-muted-foreground">
                      {referrals
                        ? t(A.refFriends, { months: MIN_REFERRAL_MONTHS })
                        : t(A.refGoesUp, { months: MIN_REFERRAL_MONTHS })}
                    </span>
                  </span>
                  {freshReferrals > 0 && (
                    <span className="shrink-0 rounded-full border border-success-border px-2 py-0.5 text-xs font-semibold text-success">
                      {t(A.plusNew, { count: freshReferrals })}
                    </span>
                  )}
                </div>
                {freeMonths > 0 && (
                  <p className="mt-3 flex items-center gap-2 rounded-md border border-success-border bg-success-muted px-3 py-2 text-sm text-success">
                    <ApperIcon name="Gift" size={16} className="shrink-0" />
                    {t(A.freeMonths, { count: freeMonths })}
                  </p>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t(A.onceActive)}{' '}
                <Link to="/account/referrals" className="text-primary hover:underline">
                  {t(A.howRef)}
                </Link>
              </p>
            )}
          </Panel>
          </div>

          <Panel title={t(A.shortcuts)} icon="Sparkles" bodyClassName="grid gap-2 py-3">
            {QUICK.map((q) => (
              <Link
                key={q.to}
                to={q.to}
                className="flex items-center gap-3 rounded-md border border-border px-3 py-2.5 transition-colors duration-(--transition-fast) hover:bg-accent hover:text-accent-foreground"
              >
                <ApperIcon name={q.icon} size={18} className="text-primary" />
                <span className="min-w-0">
                  <span className="block text-sm font-medium">{t(q.label)}</span>
                  <span className="block text-xs text-muted-foreground">{t(q.hint)}</span>
                </span>
              </Link>
            ))}
          </Panel>
        </div>
      </div>
    </div>
  );
}
